// ゆがみ (Liquify) の純ロジック。変位場 (pull 方式: out(p) = src(p + d(p))) をブラシで編集し、
// バイリニア補間で描く。Node でテストできるよう DOM に依存しない。

export interface Displacement {
  w: number
  h: number
  dx: Float32Array
  dy: Float32Array
}

export type LiquifyTool = 'warp' | 'reconstruct' | 'twirlCw' | 'twirlCcw' | 'pucker' | 'bloat'

export function createDisplacement(w: number, h: number): Displacement {
  return { w, h, dx: new Float32Array(w * h), dy: new Float32Array(w * h) }
}

/** ブラシの減衰: 中心 1、縁 0 の滑らかな山 */
function falloff(d: number, r: number): number {
  if (d >= r) return 0
  const t = 1 - (d * d) / (r * r)
  return t * t
}

export interface BrushOp {
  tool: LiquifyTool
  x: number
  y: number
  radius: number
  /** 0..1 */
  pressure: number
  /** warp 用: 今回の移動量 */
  mx?: number
  my?: number
  /** 0..255、0 = 凍結 (動かさない)。省略時は全て有効 */
  freeze?: Uint8ClampedArray | null
}

/** ブラシ 1 回分を変位場に適用し、変わった範囲を返す (無ければ null) */
export function applyBrush(f: Displacement, op: BrushOp): { x: number; y: number; w: number; h: number } | null {
  const r = op.radius
  const x0 = Math.max(0, Math.floor(op.x - r)), x1 = Math.min(f.w - 1, Math.ceil(op.x + r))
  const y0 = Math.max(0, Math.floor(op.y - r)), y1 = Math.min(f.h - 1, Math.ceil(op.y + r))
  if (x1 < x0 || y1 < y0) return null
  const p = op.pressure
  for (let y = y0; y <= y1; y++) {
    for (let x = x0; x <= x1; x++) {
      const px = x - op.x, py = y - op.y
      const d = Math.hypot(px, py)
      let k = falloff(d, r) * p
      if (k <= 0) continue
      const i = y * f.w + x
      if (op.freeze) k *= op.freeze[i]! / 255
      if (k <= 0) continue
      switch (op.tool) {
        case 'warp':
          // 画素を移動方向へ押す = 出力はその分だけ手前からサンプルする
          f.dx[i] = f.dx[i]! - (op.mx ?? 0) * k
          f.dy[i] = f.dy[i]! - (op.my ?? 0) * k
          break
        case 'reconstruct': {
          const s = 1 - Math.min(1, k * 0.5)
          f.dx[i] = f.dx[i]! * s
          f.dy[i] = f.dy[i]! * s
          break
        }
        case 'twirlCw':
        case 'twirlCcw': {
          const a = k * 0.12 * (op.tool === 'twirlCw' ? 1 : -1)
          const c = Math.cos(a), s = Math.sin(a)
          // 出力 p はソースの (p-c) を -a 回転した位置から取る → 見た目は a 回転
          const rx = px * c + py * s, ry = -px * s + py * c
          f.dx[i] = f.dx[i]! + (rx - px)
          f.dy[i] = f.dy[i]! + (ry - py)
          break
        }
        case 'pucker': {
          const s = k * 0.08
          f.dx[i] = f.dx[i]! + px * s
          f.dy[i] = f.dy[i]! + py * s
          break
        }
        case 'bloat': {
          const s = k * 0.08
          f.dx[i] = f.dx[i]! - px * s
          f.dy[i] = f.dy[i]! - py * s
          break
        }
      }
    }
  }
  return { x: x0, y: y0, w: x1 - x0 + 1, h: y1 - y0 + 1 }
}

export function resetDisplacement(f: Displacement): void {
  f.dx.fill(0)
  f.dy.fill(0)
}

/** 変位場をバイリニアで読む (場の座標系) */
function sampleField(f: Displacement, x: number, y: number): [number, number] {
  const cx = Math.max(0, Math.min(f.w - 1.001, x)), cy = Math.max(0, Math.min(f.h - 1.001, y))
  const x0 = Math.floor(cx), y0 = Math.floor(cy)
  const tx = cx - x0, ty = cy - y0
  const x1 = Math.min(f.w - 1, x0 + 1), y1 = Math.min(f.h - 1, y0 + 1)
  const i00 = y0 * f.w + x0, i10 = y0 * f.w + x1, i01 = y1 * f.w + x0, i11 = y1 * f.w + x1
  const dx = (f.dx[i00]! * (1 - tx) + f.dx[i10]! * tx) * (1 - ty) + (f.dx[i01]! * (1 - tx) + f.dx[i11]! * tx) * ty
  const dy = (f.dy[i00]! * (1 - tx) + f.dy[i10]! * tx) * (1 - ty) + (f.dy[i01]! * (1 - tx) + f.dy[i11]! * tx) * ty
  return [dx, dy]
}

/**
 * src (w×h RGBA) を変位場で描いて out の同じ位置に書く。region は画像座標。
 * fieldScale = 場の解像度 / 画像の解像度 (プロキシ編集を全解像度へ適用するときに < 1 以外)
 */
export function renderRegion(src: Uint8ClampedArray, w: number, h: number, f: Displacement, region: { x: number; y: number; w: number; h: number }, out: Uint8ClampedArray, fieldScale = 1): void {
  const x0 = Math.max(0, region.x), y0 = Math.max(0, region.y)
  const x1 = Math.min(w, region.x + region.w), y1 = Math.min(h, region.y + region.h)
  const inv = 1 / fieldScale
  for (let y = y0; y < y1; y++) {
    for (let x = x0; x < x1; x++) {
      let dx: number, dy: number
      if (fieldScale === 1) {
        const i = y * f.w + x
        dx = f.dx[i]!; dy = f.dy[i]!
      } else {
        const [a, b] = sampleField(f, x * fieldScale, y * fieldScale)
        dx = a * inv; dy = b * inv
      }
      const o = (y * w + x) * 4
      if (dx === 0 && dy === 0) {
        out[o] = src[o]!; out[o + 1] = src[o + 1]!; out[o + 2] = src[o + 2]!; out[o + 3] = src[o + 3]!
        continue
      }
      const sx = Math.max(0, Math.min(w - 1.001, x + dx)), sy = Math.max(0, Math.min(h - 1.001, y + dy))
      const ix = Math.floor(sx), iy = Math.floor(sy)
      const tx = sx - ix, ty = sy - iy
      const ix1 = Math.min(w - 1, ix + 1), iy1 = Math.min(h - 1, iy + 1)
      const i00 = (iy * w + ix) * 4, i10 = (iy * w + ix1) * 4, i01 = (iy1 * w + ix) * 4, i11 = (iy1 * w + ix1) * 4
      // アルファ加重で色を混ぜる (透明の黒がにじまないように)
      const a00 = src[i00 + 3]! * (1 - tx) * (1 - ty), a10 = src[i10 + 3]! * tx * (1 - ty), a01 = src[i01 + 3]! * (1 - tx) * ty, a11 = src[i11 + 3]! * tx * ty
      const asum = a00 + a10 + a01 + a11
      if (asum <= 0) {
        out[o] = 0; out[o + 1] = 0; out[o + 2] = 0; out[o + 3] = 0
        continue
      }
      for (let c = 0; c < 3; c++) out[o + c] = (src[i00 + c]! * a00 + src[i10 + c]! * a10 + src[i01 + c]! * a01 + src[i11 + c]! * a11) / asum
      out[o + 3] = asum
    }
  }
}
